import React from "react";
import { Tag } from "antd";
import { ShoppingBag } from "lucide-react";
import { TableV2 } from "../../../components";

const formatPrice = (v: number) =>
  `LKR ${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export const OrderItemsTable = ({ order }: any) => {
  const returnedQtyMap: Record<string, number> = {};

  (order?.returns || []).forEach((ret: any) => {
    (ret.items || []).forEach((ri: any) => {
      returnedQtyMap[ri.order_item_id] =
        (returnedQtyMap[ri.order_item_id] || 0) + ri.quantity;
    });
  });

  const column = [
    {
      title: "Product",
      key: "product",
      render: (_: any, rec: any) => (
        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-gray-800 text-xs">
              {rec.product_name}
            </span>
            {rec.variant_name && (
              <Tag color="purple" className="text-[10px] font-semibold px-1.5 py-0 border-purple-200 m-0">
                {rec.variant_name}
              </Tag>
            )}
          </div>
          <div className="text-[10px] text-gray-400 mt-0.5">
            SKU: {rec.sku_code || "N/A"} | Batch: {rec.batch_no || "N/A"}
          </div>
          {rec.description && (
            <div className="text-[10px] text-indigo-600 bg-indigo-50/40 border border-indigo-100 rounded px-1.5 py-0.5 mt-1 font-medium italic break-words max-w-[200px]">
              Note: {rec.description}
            </div>
          )}
          {(() => {
            let addData = rec.additional_data;
            if (typeof addData === "string") {
              try {
                addData = JSON.parse(addData);
              } catch {
                addData = null;
              }
            }
            if (!addData || typeof addData !== "object") return null;

            return (
              <div className="text-[10px] text-indigo-700 font-medium mt-1 flex flex-wrap gap-1">
                {addData.cut_length_ft > 0 && (
                  <span className="bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded text-[9px]">
                    Cut: {addData.cut_length_ft} ft
                  </span>
                )}
                {addData.piece_count > 0 && (
                  <span className="bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded text-[9px]">
                    Pcs: {addData.piece_count}
                  </span>
                )}
                {addData.total_length_ft > 0 && (
                  <span className="bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded text-[9px] font-bold">
                    Total: {addData.total_length_ft} ft
                  </span>
                )}
                {addData.gauge && (
                  <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[9px]">
                    Gauge: {addData.gauge} mm
                  </span>
                )}
                {addData.color && (
                  <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[9px]">
                    Color: {addData.color}
                  </span>
                )}
                {addData.width_ft > 0 && (
                  <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[9px]">
                    Width: {addData.width_ft} ft
                  </span>
                )}
                {addData.curve_length_ft > 0 && (
                  <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[9px]">
                    Curve: {addData.curve_length_ft} ft
                  </span>
                )}
                {addData.delivery_date && (
                  <span className="bg-cyan-50 text-cyan-700 border border-cyan-100 px-1.5 py-0.5 rounded text-[9px] font-semibold">
                    Delivery: {addData.delivery_date}
                  </span>
                )}
                {rec.is_cut === 1 || addData.is_cut === 1 ? (
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded text-[9px] font-bold">
                    ✓ Cut
                  </span>
                ) : (
                  <span className="bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded text-[9px] font-semibold">
                    Not Cut
                  </span>
                )}
              </div>
            );
          })()}
          {(returnedQtyMap[rec.id] ?? 0) > 0 && (
            <Tag color="orange" className="mt-1 text-[9px]">
              {returnedQtyMap[rec.id]} returned
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Cut Status",
      key: "is_cut",
      render: (_: any, rec: any) => {
        let addData = rec.additional_data;
        if (typeof addData === "string") {
          try {
            addData = JSON.parse(addData);
          } catch {
            addData = null;
          }
        }
        const isCut =
          rec.is_cut === 1 ||
          rec.is_cut === true ||
          Number(rec.is_cut) === 1 ||
          addData?.is_cut === 1;

        return isCut ? (
          <Tag color="success" className="font-semibold text-xs m-0">
            Cut
          </Tag>
        ) : (
          <Tag color="warning" className="font-medium text-xs m-0">
            Not Cut
          </Tag>
        );
      },
    },
    {
      title: "Price",
      key: "price",
      render: (_: any, rec: any) => formatPrice(rec.price),
    },
    {
      title: "Qty",
      key: "quantity",
      render: (_: any, rec: any) => (
        <Tag color="blue" className="font-semibold text-xs">
          {rec.quantity} {rec.unit || ""}
        </Tag>
      ),
    },
    {
      title: "Discount",
      key: "discount_amount",
      render: (_: any, rec: any) =>
        rec.discount_amount > 0 ? `-${formatPrice(rec.discount_amount)}` : "-",
    },
    {
      title: "Sold Price",
      key: "sold_price",
      render: (_: any, rec: any) =>
        rec.sold_price > 0 ? formatPrice(rec.sold_price) : "-",
    },
    {
      title: "Total Price",
      key: "total_price",
      render: (_: any, rec: any) => (
        <span className="font-bold text-gray-800 text-xs">
          {formatPrice(rec.total_price)}
        </span>
      ),
    },
  ];

  return (
    <>
      <div>
        <h3 className="text-xs font-bold text-gray-800 mb-2 flex items-center gap-1.5">
          <ShoppingBag className="w-3.5 h-3.5 text-gray-400" />
          Sold Product Items ({order.items?.length || 0})
        </h3>
        <TableV2
          dataSource={order.items || []}
          columns={column}
          pagination={{ pageSize: 10, size: "small" }}
          size="small"
          rowKey="id"
          bordered
          scroll={{ x: 900 }}
          mobileColumn={["product"]}
        />
      </div>
    </>
  );
};